// "My saved tags" — a compact list of community tags the *current signed-in
// user* has previously submitted that fall on (or near) the currently
// selected route polyline. Pulls directly from `community_tags` filtered to
// `auth.uid()`, then snaps each to the route to filter by buffer + sort by
// distance along.

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, MapPin, Pencil, ThumbsDown, ThumbsUp, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { nearestPointOnPolyline } from "@/utils/polyline";
import type { LngLat } from "@/utils/polyline";
import type { CommunityTag, Sentiment } from "@/api/community";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface Props {
  /** Coordinates of the currently selected route. Tags within `bufferM` of
   *  this polyline are listed in order of how far along they are. */
  routeCoords: LngLat[];
  /** Buffer in meters — only show tags within this distance of the route. */
  bufferM?: number;
  /** Tap a row → host pans the map and opens that signal's popup. */
  onFocus: (tagId: string) => void;
}

function fmtAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months <= 1 ? "1mo ago" : `${months}mo ago`;
}

export const MySavedTagsAlongRoute = ({ routeCoords, bufferM = 120, onFocus }: Props) => {
  const { user, loading: authLoading } = useAuth();
  const [myTags, setMyTags] = useState<CommunityTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Inline edit dialog state.
  const [editing, setEditing] = useState<CommunityTag | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editSentiment, setEditSentiment] = useState<Sentiment>("positive");
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (tag: CommunityTag) => {
    setEditing(tag);
    setEditLabel(tag.tag_label);
    setEditSentiment((tag.sentiment as Sentiment) ?? "positive");
  };

  const saveEdit = async () => {
    if (!editing) return;
    const label = editLabel.trim();
    if (label.length < 1 || label.length > 60) {
      toast.error("Label must be 1–60 characters");
      return;
    }
    setSavingEdit(true);
    const { error } = await supabase
      .from("community_tags")
      .update({ tag_label: label, sentiment: editSentiment })
      .eq("id", editing.id);
    setSavingEdit(false);
    if (error) {
      toast.error("Couldn't update tag", { description: error.message });
      return;
    }
    setMyTags((cur) =>
      cur.map((t) =>
        t.id === editing.id ? { ...t, tag_label: label, sentiment: editSentiment } : t,
      ),
    );
    toast.success("Tag updated");
    setEditing(null);
  };

  // Bbox of the route — used to scope the query so we don't pull every tag
  // the user has ever made across the world.
  const bbox = useMemo(() => {
    if (routeCoords.length < 2) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const [lng, lat] of routeCoords) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
    // Pad by ~2km so tags right at the edge still come back.
    const padLat = 0.02;
    const padLng = 0.02;
    return {
      minLat: minLat - padLat,
      maxLat: maxLat + padLat,
      minLng: minLng - padLng,
      maxLng: maxLng + padLng,
    };
  }, [routeCoords]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !bbox) {
      setMyTags([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("community_tags")
        .select("id,lat,lng,travel_mode,tag_label,sentiment,created_at,pleasantness,report_kind,reporter_weight")
        .eq("user_id", user.id)
        .gte("lat", bbox.minLat).lte("lat", bbox.maxLat)
        .gte("lng", bbox.minLng).lte("lng", bbox.maxLng)
        .order("created_at", { ascending: false })
        .limit(200);
      if (cancelled) return;
      if (error) {
        console.warn("MySavedTagsAlongRoute fetch failed:", error.message);
        setMyTags([]);
      } else {
        setMyTags((data ?? []) as CommunityTag[]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, authLoading, bbox]);

  const matched = useMemo(() => {
    if (routeCoords.length < 2 || myTags.length === 0) return [];
    return myTags
      .map((t) => {
        const snap = nearestPointOnPolyline([t.lng, t.lat], routeCoords);
        return { tag: t, distance: snap.distance, along: snap.along };
      })
      .filter((m) => m.distance <= bufferM)
      .sort((a, b) => a.along - b.along);
  }, [myTags, routeCoords, bufferM]);

  // Hide entirely when not signed in or nothing matched — keeps the route
  // panel uncluttered for users with no relevant history.
  if (!user || matched.length === 0) {
    if (loading) {
      return (
        <div className="bg-card border border-border rounded-2xl shadow-soft px-4 py-3 text-xs text-muted-foreground">
          Loading your saved tags…
        </div>
      );
    }
    return null;
  }

  const visible = expanded ? matched : matched.slice(0, 3);

  return (
    <div className="bg-card border border-primary/30 rounded-2xl shadow-soft overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="min-w-0 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-3.5 h-3.5" />
          </span>
          <div>
            <div className="text-sm font-medium">My saved tags on this route</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {matched.length} {matched.length === 1 ? "tag" : "tags"} you've left along this stretch
            </div>
          </div>
        </div>
        {matched.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            {expanded ? "Show less" : `Show all ${matched.length}`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      <ul className="divide-y divide-border">
        {visible.map(({ tag, distance }) => {
          const positive = tag.sentiment === "positive";
          return (
            <li key={tag.id} className="flex items-stretch">
              <button
                type="button"
                onClick={() => onFocus(tag.id)}
                className="flex-1 min-w-0 text-left px-4 py-2.5 hover:bg-muted/60 transition-smooth flex items-center gap-3"
              >
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    positive
                      ? "bg-sentiment-positive/15 text-sentiment-positive"
                      : "bg-sentiment-negative/15 text-sentiment-negative"
                  }`}
                >
                  {positive ? <ThumbsUp className="w-3.5 h-3.5" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{tag.tag_label}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span>{fmtAge(tag.created_at)}</span>
                    {tag.report_kind && (
                      <span className="capitalize">· {tag.report_kind.replace(/_/g, " ")}</span>
                    )}
                    <span>· {Math.round(distance)} m off route</span>
                  </div>
                </div>
                <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openEdit(tag); }}
                aria-label="Edit tag"
                className="px-3 text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-smooth flex items-center justify-center border-l border-border"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </li>
          );
        })}
      </ul>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit your tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label htmlFor="edit-tag-label" className="text-xs uppercase tracking-wide text-muted-foreground">
                Label
              </label>
              <Input
                id="edit-tag-label"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                maxLength={60}
                placeholder="e.g. Pretty street, Heavy traffic"
                className="rounded-xl"
              />
              <div className="text-[11px] text-muted-foreground text-right">{editLabel.length}/60</div>
            </div>
            <div className="space-y-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Sentiment</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEditSentiment("positive")}
                  className={`h-11 rounded-xl border flex items-center justify-center gap-2 text-sm transition-smooth ${
                    editSentiment === "positive"
                      ? "bg-sentiment-positive text-primary-foreground border-transparent"
                      : "bg-card text-foreground border-border hover:border-sentiment-positive"
                  }`}
                >
                  <ThumbsUp className="w-4 h-4" /> Positive
                </button>
                <button
                  type="button"
                  onClick={() => setEditSentiment("negative")}
                  className={`h-11 rounded-xl border flex items-center justify-center gap-2 text-sm transition-smooth ${
                    editSentiment === "negative"
                      ? "bg-sentiment-negative text-primary-foreground border-transparent"
                      : "bg-card text-foreground border-border hover:border-sentiment-negative"
                  }`}
                >
                  <ThumbsDown className="w-4 h-4" /> Negative
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-pleasant text-primary-foreground">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
